import { Button, Input, Select } from '../../../design-system';

// Org-chart vocab — mirrors the enums in models/Client.js.
export const UNIT_TYPES = [
  { value: 'division', label: 'Division' },
  { value: 'department', label: 'Department' },
  { value: 'team', label: 'Team' },
  { value: 'cost_centre', label: 'Cost Centre' },
  { value: 'branch', label: 'Branch' },
  { value: 'other', label: 'Other' },
];

export const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'intern', label: 'Intern' },
  { value: 'other', label: 'Other' },
];

// Stable client-side id for the intra-array links (see the model's note on why
// we don't use the Mongoose _id). Prefixed so unit/employee keys never collide.
const newKey = (prefix) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const emptyUnit = () => ({ key: newKey('u'), name: '', type: 'department', parentKey: '', headEmployeeKey: '', costCentreCode: '' });
export const emptyEmployee = () => ({ key: newKey('e'), name: '', designation: '', grade: '', orgUnitKey: '', managerKey: '', employmentType: 'full_time', startDate: '', endDate: '' });

const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

// Server doc -> form-friendly state. Backfills a key for any legacy row that
// somehow lacks one, so the selects always have something to reference.
export const hydrateOrgChart = (c) => ({
  orgUnits: (c.orgUnits || []).map((u) => ({
    key: u.key || newKey('u'), name: u.name || '', type: u.type || 'department',
    parentKey: u.parentKey || '', headEmployeeKey: u.headEmployeeKey || '', costCentreCode: u.costCentreCode || '',
  })),
  employees: (c.employees || []).map((e) => ({
    key: e.key || newKey('e'), name: e.name || '', designation: e.designation || '', grade: e.grade || '',
    orgUnitKey: e.orgUnitKey || '', managerKey: e.managerKey || '', employmentType: e.employmentType || 'full_time',
    startDate: toDateInput(e.startDate), endDate: toDateInput(e.endDate),
  })),
});

// Form state -> payload: drop blank rows, turn '' links into null, drop empty
// dates. Links that point at a dropped row are left as-is (they just render
// blank) — the on-remove cleanup below already nulls the common cases.
export const cleanOrgChart = ({ orgUnits, employees }) => {
  const orNull = (v) => (v ? v : null);
  return {
    orgUnits: orgUnits
      .filter((u) => u.name.trim())
      .map((u) => ({
        key: u.key, name: u.name.trim(), type: u.type,
        parentKey: orNull(u.parentKey), headEmployeeKey: orNull(u.headEmployeeKey),
        ...(u.costCentreCode.trim() ? { costCentreCode: u.costCentreCode.trim() } : {}),
      })),
    employees: employees
      .filter((e) => e.name.trim())
      .map((e) => {
        const out = {
          key: e.key, name: e.name.trim(), employmentType: e.employmentType,
          orgUnitKey: orNull(e.orgUnitKey), managerKey: orNull(e.managerKey),
        };
        if (e.designation.trim()) out.designation = e.designation.trim();
        if (e.grade.trim()) out.grade = e.grade.trim();
        if (e.startDate) out.startDate = e.startDate;
        if (e.endDate) out.endDate = e.endDate;
        return out;
      }),
  };
};

export default function OrgChartFields({ orgUnits, employees, onUnitsChange, onEmployeesChange }) {
  // --- units ---
  const setUnitAt = (idx, field, value) =>
    onUnitsChange(orgUnits.map((u, i) => (i === idx ? { ...u, [field]: value } : u)));
  const addUnit = () => onUnitsChange([...orgUnits, emptyUnit()]);
  const removeUnit = (idx) => {
    const gone = orgUnits[idx].key;
    // Don't orphan the tree: children lose their parent, employees lose their
    // unit. (headEmployeeKey points at an employee, so it's unaffected here.)
    onUnitsChange(
      orgUnits.filter((_, i) => i !== idx).map((u) => (u.parentKey === gone ? { ...u, parentKey: '' } : u))
    );
    onEmployeesChange(employees.map((e) => (e.orgUnitKey === gone ? { ...e, orgUnitKey: '' } : e)));
  };

  // --- employees ---
  const setEmpAt = (idx, field, value) =>
    onEmployeesChange(employees.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  const addEmp = () => onEmployeesChange([...employees, emptyEmployee()]);
  const removeEmp = (idx) => {
    const gone = employees[idx].key;
    // Reports lose their manager; any unit headed by them loses its head.
    onEmployeesChange(
      employees.filter((_, i) => i !== idx).map((e) => (e.managerKey === gone ? { ...e, managerKey: '' } : e))
    );
    onUnitsChange(orgUnits.map((u) => (u.headEmployeeKey === gone ? { ...u, headEmployeeKey: '' } : u)));
  };

  return (
    <>
      {/* Organisation units */}
      <div className="gstins-subsection">
        <div className="apob-header">
          <span className="ds-field-label">Organisation Units</span>
          <Button type="button" variant="secondary" size="sm" onClick={addUnit}>+ Add Unit</Button>
        </div>
        {orgUnits.length === 0 && (
          <p className="hint-text">No units recorded. Add divisions, departments, teams or cost centres — a unit can sit under a parent unit to form the tree.</p>
        )}
        {orgUnits.map((u, idx) => (
          <div key={u.key} className="gstin-edit-block">
            <div className="gstin-edit-head">
              <span className="ds-field-label">Unit {idx + 1}</span>
              <button type="button" onClick={() => removeUnit(idx)} className="btn-remove-icon">×</button>
            </div>
            <div className="form-row">
              <Input label="Name" value={u.name} onChange={(e) => setUnitAt(idx, 'name', e.target.value)} />
              <Select label="Type" value={u.type} onChange={(e) => setUnitAt(idx, 'type', e.target.value)}>
                {UNIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Input label="Cost Centre Code" value={u.costCentreCode} onChange={(e) => setUnitAt(idx, 'costCentreCode', e.target.value)} />
            </div>
            <div className="form-row">
              <Select label="Parent Unit" value={u.parentKey} onChange={(e) => setUnitAt(idx, 'parentKey', e.target.value)}>
                <option value="">-- Top level --</option>
                {orgUnits.filter((o) => o.key !== u.key && o.name.trim()).map((o) => (
                  <option key={o.key} value={o.key}>{o.name}</option>
                ))}
              </Select>
              <Select label="Head of Unit" value={u.headEmployeeKey} onChange={(e) => setUnitAt(idx, 'headEmployeeKey', e.target.value)}>
                <option value="">-- None --</option>
                {employees.filter((emp) => emp.name.trim()).map((emp) => (
                  <option key={emp.key} value={emp.key}>{emp.name}</option>
                ))}
              </Select>
            </div>
          </div>
        ))}
      </div>

      {/* Employees / reporting lines */}
      <div className="gstins-subsection">
        <div className="apob-header">
          <span className="ds-field-label">People &amp; Reporting Lines</span>
          <Button type="button" variant="secondary" size="sm" onClick={addEmp}>+ Add Person</Button>
        </div>
        {employees.length === 0 && (
          <p className="hint-text">No people recorded. Add employees, set who they report to, and which unit they sit in — the reporting line is independent of the unit, so dotted-line managers work.</p>
        )}
        {employees.map((emp, idx) => (
          <div key={emp.key} className="gstin-edit-block">
            <div className="gstin-edit-head">
              <span className="ds-field-label">Person {idx + 1}</span>
              <button type="button" onClick={() => removeEmp(idx)} className="btn-remove-icon">×</button>
            </div>
            <div className="form-row">
              <Input label="Name" value={emp.name} onChange={(e) => setEmpAt(idx, 'name', e.target.value)} />
              <Input label="Designation" value={emp.designation} onChange={(e) => setEmpAt(idx, 'designation', e.target.value)} />
              <Input label="Grade" value={emp.grade} onChange={(e) => setEmpAt(idx, 'grade', e.target.value)} />
              <Select label="Employment Type" value={emp.employmentType} onChange={(e) => setEmpAt(idx, 'employmentType', e.target.value)}>
                {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </div>
            <div className="form-row">
              <Select label="Unit" value={emp.orgUnitKey} onChange={(e) => setEmpAt(idx, 'orgUnitKey', e.target.value)}>
                <option value="">-- None --</option>
                {orgUnits.filter((u) => u.name.trim()).map((u) => (
                  <option key={u.key} value={u.key}>{u.name}</option>
                ))}
              </Select>
              <Select label="Reports To" value={emp.managerKey} onChange={(e) => setEmpAt(idx, 'managerKey', e.target.value)}>
                <option value="">-- None --</option>
                {employees.filter((m) => m.key !== emp.key && m.name.trim()).map((m) => (
                  <option key={m.key} value={m.key}>{m.name}</option>
                ))}
              </Select>
              <Input label="Start Date" type="date" value={emp.startDate} onChange={(e) => setEmpAt(idx, 'startDate', e.target.value)} />
              <Input label="End Date" type="date" value={emp.endDate} onChange={(e) => setEmpAt(idx, 'endDate', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
